import React, { useState } from 'react';
import { ContentItem, ContentType, ContentTypeLabels } from '@memriti/shared';

interface ByteCardProps {
  content: ContentItem;
  onNext: () => void;
  onPrevious: () => void;
  currentIndex: number;
  totalCount: number;
}

const ByteCard: React.FC<ByteCardProps> = ({
  content,
  onNext,
  onPrevious,
  currentIndex,
  totalCount,
}) => {
  const [isAnimating, setIsAnimating] = useState(false);

  const getContentText = () => {
    switch (content.type) {
      case ContentType.EXPLANATION:
        return (content.payload as any)?.explanation || 'No explanation available';
      case ContentType.COMMON_MISTAKES:
        const mistake = (content.payload as any)?.mistake || 'Common mistake';
        const mistakeExplanation = (content.payload as any)?.explanation || 'No explanation available';
        return `${mistake}\n\n${mistakeExplanation}`;
      case ContentType.TIPS_TRICKS:
        const tips = (content.payload as any)?.tips;
        return Array.isArray(tips) ? tips.join('\n\n') : 'No tips available';
      default:
        return 'Content not available';
    }
  };

  const getContentIcon = () => {
    switch (content.type) {
      case ContentType.EXPLANATION:
        return (
          <div className="w-10 h-10 bg-gradient-to-br from-sky-400 to-sky-500 rounded-xl flex items-center justify-center shadow-md">
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        );
      case ContentType.COMMON_MISTAKES:
        return (
          <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-amber-500 rounded-xl flex items-center justify-center shadow-md">
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
        );
      case ContentType.TIPS_TRICKS:
        return (
          <div className="w-10 h-10 bg-gradient-to-br from-emerald-400 to-emerald-500 rounded-xl flex items-center justify-center shadow-md">
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
          </div>
        );
      default:
        return null;
    }
  };

  const getContentColor = () => {
    switch (content.type) {
      case ContentType.EXPLANATION:
        return 'from-sky-50 via-sky-25 to-white dark:from-sky-900/10 dark:via-sky-800/5 dark:to-gray-900/5';
      case ContentType.COMMON_MISTAKES:
        return 'from-amber-50 via-amber-25 to-white dark:from-amber-900/10 dark:via-amber-800/5 dark:to-gray-900/5';
      case ContentType.TIPS_TRICKS:
        return 'from-emerald-50 via-emerald-25 to-white dark:from-emerald-900/10 dark:via-emerald-800/5 dark:to-gray-900/5';
      default:
        return 'from-gray-50 via-gray-25 to-white dark:from-gray-900/10 dark:via-gray-800/5 dark:to-gray-900/5';
    }
  };

  const getContentBorderColor = () => {
    switch (content.type) {
      case ContentType.EXPLANATION:
        return 'border-sky-200/50 dark:border-sky-700/30';
      case ContentType.COMMON_MISTAKES:
        return 'border-amber-200/50 dark:border-amber-700/30';
      case ContentType.TIPS_TRICKS:
        return 'border-emerald-200/50 dark:border-emerald-700/30';
      default:
        return 'border-gray-200/50 dark:border-gray-700/30';
    }
  };

  const getContentTextColor = () => {
    switch (content.type) {
      case ContentType.EXPLANATION:
        return 'text-sky-900 dark:text-sky-50';
      case ContentType.COMMON_MISTAKES:
        return 'text-amber-900 dark:text-amber-50';
      case ContentType.TIPS_TRICKS:
        return 'text-emerald-900 dark:text-emerald-50';
      default:
        return 'text-gray-900 dark:text-gray-50';
    }
  };

  const getContentAccentColor = () => {
    switch (content.type) {
      case ContentType.EXPLANATION:
        return 'text-sky-600 dark:text-sky-400';
      case ContentType.COMMON_MISTAKES:
        return 'text-amber-600 dark:text-amber-400';
      case ContentType.TIPS_TRICKS:
        return 'text-emerald-600 dark:text-emerald-400';
      default:
        return 'text-gray-600 dark:text-gray-400';
    }
  };

  const handleSwipe = (direction: 'left' | 'right') => {
    setIsAnimating(true);
    setTimeout(() => {
      if (direction === 'left') {
        onNext();
      } else {
        onPrevious();
      }
      setIsAnimating(false);
    }, 300);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowLeft') {
      handleSwipe('left');
    } else if (e.key === 'ArrowRight') {
      handleSwipe('right');
    }
  };

  const contentText = getContentText();

  // Determine background color and style based on content type and dark mode
  let cardBg = "bg-white dark:bg-gray-900";
  let cardStyle = {};
  if (content.type === ContentType.EXPLANATION) cardBg = "bg-sky-50 dark:bg-sky-900/20";
  if (content.type === ContentType.COMMON_MISTAKES) {
    cardBg = "bg-rose-50";
    cardStyle = { ...(cardStyle as any), '--tw-dark-bg': 'rgba(251,113,133,0.08)' };
  }
  if (content.type === ContentType.TIPS_TRICKS) {
    cardBg = "bg-emerald-50";
    cardStyle = { ...(cardStyle as any), '--tw-dark-bg': 'rgba(52,211,153,0.08)' };
  }

  return (
    <div
      className="relative w-full max-w-none md:max-w-screen-lg mx-auto min-h-[18rem] my-6 flex flex-col items-center justify-center px-2 sm:px-0"
      onKeyDown={handleKeyPress}
      tabIndex={0}
    >
      {/* Progress Indicator */}
      <div className="absolute top-2 sm:top-4 left-1/2 transform -translate-x-1/2 z-10">
        <div className="flex space-x-1">
          {Array.from({ length: totalCount }, (_, i) => (
            <div
              key={i}
              className={`w-2 h-1.5 rounded-full transition-all duration-300 ${
                i === currentIndex
                  ? 'bg-blue-500 scale-110'
                  : i < currentIndex
                  ? 'bg-blue-300'
                  : 'bg-gray-200 dark:bg-gray-700'
              }`}
            />
          ))}
        </div>
      </div>

      {/* Main Card */}
      <div
        className={`relative w-full ${cardBg} border border-gray-200 dark:border-gray-700 rounded-2xl shadow-xl flex flex-col items-stretch p-5 sm:p-8 md:p-12`}
        style={{
          minHeight: '320px',
          maxHeight: '80vh',
          ...(cardBg === 'bg-rose-50' && { backgroundColor: (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) ? 'rgba(251,113,133,0.08)' : undefined }),
          ...(cardBg === 'bg-emerald-50' && { backgroundColor: (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) ? 'rgba(52,211,153,0.08)' : undefined }),
        }}
      >
        {/* Header */}
        <div className="flex items-center space-x-3 mb-4">
          {getContentIcon()}
          <span className={`text-lg font-semibold uppercase tracking-wide ${getContentAccentColor()}`}>{ContentTypeLabels[content.type as ContentType]}</span>
        </div>
        <hr className="border-gray-200 dark:border-gray-700 mb-4" />
        {/* Main Content */}
        <div className="flex-1 overflow-y-auto mb-6">
          {content.type === ContentType.COMMON_MISTAKES ? (
            <div className="space-y-4">
              <div className="bg-red-50 border-l-4 border-red-400 dark:bg-red-900/20 dark:border-red-500 px-4 py-3 rounded-md">
                <div className="text-sm font-semibold uppercase text-red-700 dark:text-red-300 mb-1 tracking-wide">Common Mistake</div>
                <div className="text-lg font-medium text-red-800 dark:text-red-200 whitespace-pre-line break-words" style={{ wordBreak: 'break-word' }}>
                  {(content.payload as any)?.mistake || 'Common mistake'}
                </div>
              </div>
              <div className="text-base leading-relaxed text-gray-900 dark:text-gray-100 whitespace-pre-line break-words" style={{ wordBreak: 'break-word' }}>
                {(content.payload as any)?.explanation || 'No explanation available'}
              </div>
            </div>
          ) : content.type === ContentType.TIPS_TRICKS ? (
            <ul className="list-disc pl-6 space-y-2 text-base leading-relaxed text-gray-900 dark:text-gray-100">
              {Array.isArray((content.payload as any)?.tips)
                ? (content.payload as any).tips.map((tip: string, idx: number) => (
                    <li key={idx} className="break-words whitespace-pre-line">{tip}</li>
                  ))
                : <li>No tips available</li>}
            </ul>
          ) : (
            <span className="block text-base leading-relaxed text-gray-900 dark:text-gray-100 whitespace-pre-line break-words" style={{ wordBreak: 'break-word' }}>
              {getContentText()}
            </span>
          )}
        </div>
        {/* Navigation Buttons */}
        <div className="flex items-center justify-between mt-auto pt-2 gap-2">
          <button
            onClick={() => handleSwipe('right')}
            disabled={currentIndex === 0}
            className="flex-1 py-3 rounded-xl bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 font-medium text-base shadow-sm hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
            style={{ minWidth: 0 }}
          >
            Previous
          </button>
          <button
            onClick={() => handleSwipe('left')}
            disabled={currentIndex === totalCount - 1}
            className="flex-1 py-3 rounded-xl bg-blue-600 text-white font-medium text-base shadow-sm hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
            style={{ minWidth: 0 }}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default ByteCard;